import { useState } from 'react';
import { testKeyword } from '../../../services/projectService';

const normalizeKeywordToken = (rawValue, { forcePrefix = '' } = {}) => {
  if (typeof rawValue !== 'string') {
    return '';
  }

  let normalized = rawValue.trim().toLowerCase();
  if (!normalized) {
    return '';
  }

  if (forcePrefix && !normalized.startsWith(forcePrefix)) {
    normalized = `${forcePrefix}${normalized}`;
  }

  return normalized;
};

const normalizeKeywordArray = (value, { forcePrefix = '' } = {}) => {
  if (!Array.isArray(value)) {
    return [];
  }

  const result = [];
  const seen = new Set();

  value.forEach((item) => {
    const normalized = normalizeKeywordToken(item, { forcePrefix });
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    result.push(normalized);
  });

  return result;
};

const normalizeKeywordInput = (inputValue, { forcePrefix = '' } = {}) => {
  if (typeof inputValue !== 'string') {
    return [];
  }

  const seen = new Set();
  return inputValue
    .split(',')
    .map((chunk) => normalizeKeywordToken(chunk, { forcePrefix }))
    .filter((keyword) => {
      if (!keyword || seen.has(keyword)) {
        return false;
      }
      seen.add(keyword);
      return true;
    });
};

const mergeUniqueKeywords = (currentKeywords, keywordsToAdd) => {
  const merged = [...currentKeywords];
  const seen = new Set(currentKeywords);

  keywordsToAdd.forEach((keyword) => {
    if (!seen.has(keyword)) {
      seen.add(keyword);
      merged.push(keyword);
    }
  });

  return merged;
};

const useKeywords = (project, setProject, setError) => {
  const [newKeyword, setNewKeyword] = useState('');
  const [newPrimaryKeyword, setNewPrimaryKeyword] = useState('');
  const [newSecondaryKeyword, setNewSecondaryKeyword] = useState('');
  const [newMinusWord, setNewMinusWord] = useState('');
  const [testKeywordDialogOpen, setTestKeywordDialogOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResults, setTestResults] = useState(null);
  
  const handleAddKeyword = () => {
    if (!newKeyword) return;

    const keywordsToAdd = normalizeKeywordInput(newKeyword);

    if (keywordsToAdd.length === 0) {
      setNewKeyword('');
      return;
    }

    const currentKeywords = normalizeKeywordArray(project.keywords);

    const updatedKeywords = mergeUniqueKeywords(currentKeywords, keywordsToAdd);

    setProject({
      ...project,
      keywords: updatedKeywords
    });
    setNewKeyword('');
  };
  
  const handleDeleteKeyword = (keywordToDelete) => {
    if (!project.keywords || !Array.isArray(project.keywords)) return;
    
    setProject({
      ...project,
      keywords: project.keywords.filter(keyword => keyword !== keywordToDelete)
    });
  };
  
  const handleAddPrimaryKeyword = () => {
    if (!newPrimaryKeyword) return;

    const keywordsToAdd = normalizeKeywordInput(newPrimaryKeyword);

    if (keywordsToAdd.length === 0) {
      setNewPrimaryKeyword('');
      return;
    }

    const currentKeywords = normalizeKeywordArray(project.primary_keywords);
    const updatedKeywords = mergeUniqueKeywords(currentKeywords, keywordsToAdd);

    setProject({
      ...project,
      primary_keywords: updatedKeywords
    });
    setNewPrimaryKeyword('');
  };
  
  const handleDeletePrimaryKeyword = (keywordToDelete) => {
    if (!project.primary_keywords || !Array.isArray(project.primary_keywords)) return;
    
    setProject({
      ...project,
      primary_keywords: project.primary_keywords.filter(keyword => keyword !== keywordToDelete)
    });
  };
  
  const handleAddSecondaryKeyword = () => {
    if (!newSecondaryKeyword) return;

    const keywordsToAdd = normalizeKeywordInput(newSecondaryKeyword, { forcePrefix: '+' });

    if (keywordsToAdd.length === 0) {
      setNewSecondaryKeyword('');
      return;
    }

    const currentKeywords = normalizeKeywordArray(project.secondary_keywords, { forcePrefix: '+' });
    const updatedKeywords = mergeUniqueKeywords(currentKeywords, keywordsToAdd);

    setProject({
      ...project,
      secondary_keywords: updatedKeywords
    });
    setNewSecondaryKeyword('');
  };
  
  const handleDeleteSecondaryKeyword = (keywordToDelete) => {
    if (!project.secondary_keywords || !Array.isArray(project.secondary_keywords)) return;
    
    setProject({
      ...project,
      secondary_keywords: project.secondary_keywords.filter(keyword => keyword !== keywordToDelete)
    });
  };
  
  const handleAddMinusWord = () => {
    if (!newMinusWord) return;

    const keywordsToAdd = normalizeKeywordInput(newMinusWord, { forcePrefix: '-' });

    if (keywordsToAdd.length === 0) {
      setNewMinusWord('');
      return;
    }

    const currentKeywords = normalizeKeywordArray(project.minus_words, { forcePrefix: '-' });
    const updatedKeywords = mergeUniqueKeywords(currentKeywords, keywordsToAdd);

    setProject({
      ...project,
      minus_words: updatedKeywords
    });
    setNewMinusWord('');
  };
  
  const handleDeleteMinusWord = (keywordToDelete) => {
    if (!project.minus_words || !Array.isArray(project.minus_words)) return;
    
    setProject({
      ...project,
      minus_words: project.minus_words.filter(keyword => keyword !== keywordToDelete)
    });
  };

  const handleClearAllKeywords = () => {
    const totalKeywords =
      (Array.isArray(project.keywords) ? project.keywords.length : 0) +
      (Array.isArray(project.primary_keywords) ? project.primary_keywords.length : 0) +
      (Array.isArray(project.secondary_keywords) ? project.secondary_keywords.length : 0) +
      (Array.isArray(project.minus_words) ? project.minus_words.length : 0);

    if (totalKeywords === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Remove all ${totalKeywords} keywords from this project? This will clear legacy, primary, secondary, and minus keywords.`
    );

    if (!confirmed) {
      return;
    }

    setProject({
      ...project,
      keywords: [],
      primary_keywords: [],
      secondary_keywords: [],
      minus_words: []
    });
  };
  
  const handleOpenTestKeywordDialog = () => {
    setTestMessage('');
    setTestResults(null);
    setTestKeywordDialogOpen(true);
  };
  
  const handleCloseTestKeywordDialog = () => {
    setTestKeywordDialogOpen(false);
  };
  
  const handleTestKeyword = async (keywordTab) => {
    if (!testMessage.trim()) {
      return;
    }
    
    // Check which mode we're in
    if (keywordTab === 'legacy') {
      // Legacy mode
      if (!project.keywords || project.keywords.length === 0) {
        setError('Please add at least one keyword to test');
        return;
      }
      
      try {
        const results = await testKeyword(testMessage, project.keywords);
        setTestResults(results);
      } catch (err) {
        console.error('Error testing keyword:', err);
        setError('Error testing keywords: ' + err.message);
      }
    } else {
      // Advanced mode
      if (
        (!project.primary_keywords || project.primary_keywords.length === 0) &&
        (!project.secondary_keywords || project.secondary_keywords.length === 0)
      ) {
        setError('Please add at least one primary or secondary keyword to test');
        return;
      }
      
      try {
        // Use the advanced keyword test endpoint
        const results = await testKeyword(
          testMessage, 
          null, // No legacy keywords
          project.primary_keywords,
          project.secondary_keywords,
          project.minus_words
        );
        setTestResults(results);
      } catch (err) {
        console.error('Error testing advanced keywords:', err);
        setError('Error testing advanced keywords: ' + err.message);
      }
    }
  };
  
  return {
    newKeyword,
    setNewKeyword,
    newPrimaryKeyword,
    setNewPrimaryKeyword,
    newSecondaryKeyword,
    setNewSecondaryKeyword,
    newMinusWord,
    setNewMinusWord,
    testKeywordDialogOpen,
    testMessage,
    setTestMessage,
    testResults,
    handleAddKeyword,
    handleDeleteKeyword,
    handleAddPrimaryKeyword,
    handleDeletePrimaryKeyword,
    handleAddSecondaryKeyword,
    handleDeleteSecondaryKeyword,
    handleAddMinusWord,
    handleDeleteMinusWord,
    handleClearAllKeywords,
    handleOpenTestKeywordDialog,
    handleCloseTestKeywordDialog,
    handleTestKeyword
  };
};

export default useKeywords;
